import { createConsumer, Consumer } from "@rails/actioncable";
// Type-only: AppWorkspace imports startOrderActionCable, so a value import here
// would be a require cycle.
import type { AppWorkspace } from "../stores/AppWorkspace";
import { SwapOrderPayload } from "./types";

declare global {
  interface Window {
    __appCableConsumer?: Consumer;
  }
}

interface CableMessage {
  message: string;
  data: Record<string, any>;
}

const getConsumer = (): Consumer => {
  if (!window.__appCableConsumer) window.__appCableConsumer = createConsumer();
  return window.__appCableConsumer;
};

// Shared by both channels: AccountsChannel carries a logged-in user's whole
// dashboard, OrderChannel carries exactly one swap. The payloads are identical,
// so the handling is too.
const handleMessage = (workspace: AppWorkspace, payload: CableMessage) => {
  if (!payload?.message) return;

  switch (payload.message) {
    case "order-update": {
      workspace.upsertSwapOrder(payload.data as SwapOrderPayload);
      break;
    }
    case "deposit-seen": {
      const orderId = payload.data.order_id ?? payload.data.id;
      if (orderId) {
        workspace.markDepositSeen(orderId, payload.data);
        workspace.showSuccess("Deposit detected — confirming on chain.");
      }
      break;
    }
    case "payout-settled": {
      const orderId = payload.data.order_id ?? payload.data.id;
      if (orderId) {
        workspace.markPayoutSettled(orderId, payload.data);
        workspace.showSuccess("Lightning payout settled.");
      }
      break;
    }
    case "order-emergency": {
      const orderId = payload.data.order_id ?? payload.data.id;
      if (orderId) {
        workspace.markOrderEmergency(orderId, payload.data);
        // Only point at the refund form when the form is actually shown —
        // `attention` orders deliberately hide it and need support instead.
        workspace.showError(
          payload.data.phase === "refund_required"
            ? "This swap needs a refund address — see the refund section."
            : "This swap needs manual review. See the details on this page."
        );
      }
      break;
    }
    case "api-usage-update": {
      const prev = workspace.apiUsage?.data;
      workspace.setApiUsage({
        minute_bucket:  payload.data.minute_bucket ?? prev?.minute_bucket ?? "",
        used_weight:    payload.data.used_weight ?? prev?.used_weight ?? 0,
        weight_limit:   payload.data.weight_limit ?? prev?.weight_limit ?? 250,
        create_gate:    payload.data.create_gate ?? prev?.create_gate ?? 150,
        remaining:      payload.data.remaining ?? prev?.remaining ?? 0,
        create_blocked: payload.data.create_blocked ?? prev?.create_blocked ?? false,
        recent:         prev?.recent
      });
      break;
    }
    default:
      if (workspace.environment?.data.rails_env === "development") {
        // eslint-disable-next-line no-console
        console.warn("Unknown cable message:", payload);
      }
  }
};

// Logged-in users only: streams every order on the account.
export const startAccountsActionCable = (workspace: AppWorkspace) => {
  const consumer = getConsumer();

  consumer.subscriptions.create(
    { channel: "AccountsChannel" },
    {
      connected() {
        // eslint-disable-next-line no-console
        console.log("[cable] connected to AccountsChannel");
      },
      rejected() {
        // eslint-disable-next-line no-console
        console.warn("[cable] AccountsChannel REJECTED — not logged in?");
      },
      received: (payload: CableMessage) => handleMessage(workspace, payload)
    }
  );

  return consumer;
};

// Live updates for one swap, addressed by its public token. This is what a
// logged-out guest on /o/<token> subscribes to; without it their page is dead.
const orderSubscriptions = new Set<string>();

export const startOrderActionCable = (workspace: AppWorkspace, publicToken: string) => {
  if (!publicToken || orderSubscriptions.has(publicToken)) return;
  orderSubscriptions.add(publicToken);

  const consumer = getConsumer();

  consumer.subscriptions.create(
    { channel: "OrderChannel", public_token: publicToken },
    {
      connected() {
        // eslint-disable-next-line no-console
        console.log("[cable] connected to OrderChannel");
      },
      rejected() {
        orderSubscriptions.delete(publicToken);
        // eslint-disable-next-line no-console
        console.warn("[cable] OrderChannel REJECTED — unknown public_token");
      },
      received: (payload: CableMessage) => handleMessage(workspace, payload)
    }
  );

  return consumer;
};
